import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { ViewerTopFragmentCommonProps } from "../viewer-top-fragment/ViewerTopFragment";
import MultipleArticlesTop from "../../../MultipleArticlesTop/MultipleArticlesTop";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useMemo } from "react";
import TopFragmentWrapper from "../../../SheetTop/TopFragmentWrapper/TopFragmentWrapper";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { useTranslation } from "@coworker/locales";
import styles from "./MultipleArticlesViewerTopFragment.module.css";

export type MultipleArticlesViewerTopFragmentConfigProps = {
  field: string;
  // Enables showing the "Articles removed" inline message when the items
  // array can't be resolved anymore. Off by default so non-media task types
  // keep rendering nothing in that case, unchanged.
  isInlineMessageEnabled?: boolean;
};

type MultipleArticlesViewerTopFragmentProps = {
  configProps: MultipleArticlesViewerTopFragmentConfigProps;
  commonProps: ViewerTopFragmentCommonProps;
};

function MultipleArticlesViewerTopFragment({
  configProps,
  commonProps,
}: MultipleArticlesViewerTopFragmentProps) {
  const { field, isInlineMessageEnabled } = configProps;
  const { task } = commonProps;
  const { t } = useTranslation();
  const itemNosAndType = getTypedValue<
    { no: string; type: string; quantity?: number }[]
  >(getByPath(task, field));
  const state = getTypedValue<TaskState>(getByPath(task, "state"));

  const { items, isFetchingItems, isErrorFetchingItems } =
    useAllAttachedItems(itemNosAndType);

  const itemsWithQuantity = useMemo<(Item & { quantity?: number })[]>(() => {
    if (isLoading(itemNosAndType)) return [];
    return items.map((item) => {
      const match = itemNosAndType.find((i) => i.no === item.no);
      const rawQty = (match as { quantity?: unknown } | undefined)?.quantity;
      const parsed = typeof rawQty === "string" ? parseInt(rawQty, 10) : rawQty;
      const quantity =
        typeof parsed === "number" && Number.isFinite(parsed) && parsed >= 1
          ? parsed
          : undefined;
      return quantity !== undefined ? { ...item, quantity } : item;
    });
  }, [items, itemNosAndType]);

  if (isLoading(itemNosAndType)) {
    return null;
  }

  const removedMessage = isInlineMessageEnabled ? (
    <TopFragmentWrapper state={state}>
      <div className={styles["message"]}>
        <FixaInlineMessage
          variant="negative"
          title={t("articlesRemovedString")}
          body={t("articlesRemovedInTaskDescriptionString")}
        />
      </div>
    </TopFragmentWrapper>
  ) : null;

  if (itemNosAndType.length === 0) {
    return removedMessage;
  }

  // The task's own item references are non-empty, but if none of them
  // resolved to a real article once fetching finished without error, the
  // articles were deleted individually and the reference array just wasn't
  // updated -- show the same removed message instead of a misleading
  // "0 articles" card.
  if (
    !isFetchingItems &&
    !isErrorFetchingItems &&
    itemsWithQuantity.length === 0
  ) {
    return removedMessage;
  }

  return (
    <MultipleArticlesTop
      articles={isFetchingItems ? LOADING : itemsWithQuantity}
      state={state}
    />
  );
}

export default MultipleArticlesViewerTopFragment;
